import {
  AdmSupportVideoCallRecordingConsentApi,
  PublicSupportVideoCallRecordingConsentApi,
} from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import type { RecordingStatusView } from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";

/**
 * recordingConsentApi - the one place a room talks to the recording consent backend from, behind
 * a single RecordingConsentApi shape (D-05).
 *
 * Only transpiled handles are used (generated by `./gradlew runTranspileAll` into
 * custolink-transpiled_by_server/) - never a hand-rolled HTTP call (BASELINE rule). The client
 * never sends an identity: the endpoints derive it server-side (T-06.1-41).
 *
 * The worker adapter uses the org-scoped admin handles; the guest adapter uses only the roomId
 * public handles, so the admin surface is unreachable from a guest room.
 */

export type { RecordingStatusView };

export interface RecordingConsentApi {
  /** Stable query-cache key: "worker-<orgId>-<callId>" or "guest-<roomId>". */
  readonly key: string;
  getStatus(): Promise<RecordingStatusView>;
  decide(accepted: boolean): Promise<RecordingStatusView>;
}

/** Worker (org-scoped admin) side, backed by AdmSupportVideoCallRecordingConsentApi. */
export function workerRecordingConsentApi(orgId: number, callId: number): RecordingConsentApi {
  return {
    key: `worker-${orgId}-${callId}`,
    getStatus: () => AdmSupportVideoCallRecordingConsentApi.getRecordingStatus(orgId, callId),
    decide: (accepted) => AdmSupportVideoCallRecordingConsentApi.decideRecordingConsent(orgId, callId, accepted),
  };
}

/**
 * Guest (roomId-scoped, public) side, backed by PublicSupportVideoCallRecordingConsentApi. Must
 * never reference AdmSupportVideoCallRecordingConsentApi - the worker-only surface is unreachable
 * from here.
 */
export function guestRecordingConsentApi(roomId: string): RecordingConsentApi {
  return {
    key: `guest-${roomId}`,
    getStatus: () => PublicSupportVideoCallRecordingConsentApi.enduserGetRecordingStatus(roomId),
    decide: (accepted) => PublicSupportVideoCallRecordingConsentApi.enduserDecideRecordingConsent(roomId, accepted),
  };
}
